import {
  BarChart2,
  Building2,
  DollarSign,
  LucideIcon,
  Minus,
  Package,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Users,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import {
  aggregatesService,
  AllProviderAggregateItem,
  getDashboardTimezone,
  PlatformAggregateItem,
  ProductAggregateItem,
  ProviderAggregateItem,
} from "../../services/aggregatesService";
import { productSearchService } from "../../services/productSearchService";
import { ProductSearchItem } from "../../types/productV3";
import {
  formatCurrencyWithCommas,
  formatNumber,
  formatPercentage,
} from "../../utils/numberUtils";
import {
  useD3LineChart,
  type ChartDataPoint,
  type LineConfig,
  type XAxisTickConfig,
} from "../../hooks/useD3LineChart";

const PAGE_SIZE = 1000;

interface ChartColorToken {
  stroke: string;
  swatchClassName: string;
}

const CHART_COLORS: ChartColorToken[] = [
  { stroke: "var(--rp-chart-1)", swatchClassName: "bg-rp-chart-1" },
  { stroke: "var(--rp-chart-2)", swatchClassName: "bg-rp-chart-2" },
  { stroke: "var(--rp-chart-3)", swatchClassName: "bg-rp-chart-3" },
  { stroke: "var(--rp-chart-4)", swatchClassName: "bg-rp-chart-4" },
  { stroke: "var(--rp-chart-5)", swatchClassName: "bg-rp-chart-5" },
  { stroke: "var(--rp-chart-6)", swatchClassName: "bg-rp-chart-6" },
  { stroke: "var(--rp-chart-7)", swatchClassName: "bg-rp-chart-7" },
  { stroke: "var(--rp-chart-8)", swatchClassName: "bg-rp-chart-8" },
  { stroke: "var(--rp-chart-9)", swatchClassName: "bg-rp-chart-9" },
  { stroke: "var(--rp-chart-10)", swatchClassName: "bg-rp-chart-10" },
];

const TOTAL_CHART_COLOR: ChartColorToken = {
  stroke: "var(--rp-chart-total)",
  swatchClassName: "bg-rp-chart-total",
};

interface AdminLookup {
  providerNames: Map<number, string>;
  productNames: Map<string, string>;
  products: ProductSearchItem[];
}

interface AllProviderProductAggregateItem extends ProductAggregateItem {
  providerId: number;
}

function productKey(productType: ProductAggregateItem["productType"], productId: number): string {
  return `${productType}_${productId}`;
}

function providerChartKey(providerId: number): string {
  return `provider_${providerId}`;
}

function productLookupKey(providerId: number, productType: ProductAggregateItem["productType"], productId: number): string {
  return `${providerId}_${productType}_${productId}`;
}

function productSelectKey(product: ProductSearchItem): string {
  return productLookupKey(product.providerId, product.productType, product.productId);
}

function getProductColor(index: number): ChartColorToken {
  return CHART_COLORS[index % CHART_COLORS.length];
}

function getLocalDateBucket(period: string, timeZone: string): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period.split("T")[0] ?? period;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;
  return year && month && day ? `${year}-${month}-${day}` : period.split("T")[0] ?? period;
}

function formatAxisDateBucket(bucket: string): string {
  const [, month, day] = bucket.split("-");
  return month && day ? `${month}/${day}` : bucket;
}

function formatLatestAxisLabel(period: string, timeZone: string): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;
  const dateLabel = formatAxisDateBucket(getLocalDateBucket(period, timeZone));
  const timeLabel = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${dateLabel} ${timeLabel}`;
}

function formatPeriodWithTime(period: string, timeZone: string): string {
  const date = new Date(period);
  if (Number.isNaN(date.getTime())) return period;
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function getLatestByPeriod<T extends { period: string }>(items: T[]): T | null {
  if (items.length === 0) return null;
  const sorted = [...items].sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());
  return sorted[sorted.length - 1] ?? null;
}

function getPreviousByPeriod<T extends { period: string }>(items: T[]): T | null {
  if (items.length < 2) return null;
  const sorted = [...items].sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());
  return sorted[sorted.length - 2] ?? null;
}

function mergeLatest<T extends { period: string }>(items: T[], latest: T | null): T[] {
  if (!latest) return items;
  if (items.some((item) => item.period === latest.period)) return items;
  return [...items, latest];
}

function getLatestProviderRows(items: AllProviderAggregateItem[]): AllProviderAggregateItem[] {
  const latest = new Map<number, AllProviderAggregateItem>();
  items.forEach((item) => {
    const existing = latest.get(item.providerId);
    if (!existing || new Date(item.period).getTime() > new Date(existing.period).getTime()) {
      latest.set(item.providerId, item);
    }
  });
  return Array.from(latest.values()).sort((a, b) => b.totalAum - a.totalAum);
}

function getLatestProductRows(items: ProductAggregateItem[]): ProductAggregateItem[] {
  const latest = new Map<string, ProductAggregateItem>();
  items.forEach((item) => {
    const key = productKey(item.productType, item.productId);
    const existing = latest.get(key);
    if (!existing || new Date(item.period).getTime() > new Date(existing.period).getTime()) {
      latest.set(key, item);
    }
  });
  return Array.from(latest.values()).sort((a, b) => b.totalAum - a.totalAum);
}

function getLatestAllProviderProductRows(items: AllProviderProductAggregateItem[]): AllProviderProductAggregateItem[] {
  const latest = new Map<string, AllProviderProductAggregateItem>();
  items.forEach((item) => {
    const key = productLookupKey(item.providerId, item.productType, item.productId);
    const existing = latest.get(key);
    if (!existing || new Date(item.period).getTime() > new Date(existing.period).getTime()) {
      latest.set(key, item);
    }
  });
  return Array.from(latest.values()).sort((a, b) => b.totalAum - a.totalAum);
}

function getProductsForProviderPeriod(
  items: ProductAggregateItem[],
  providerPeriod: string
): ProductAggregateItem[] {
  return items.filter((item) => item.period === providerPeriod);
}

function fallbackProviderName(providerId: number): string {
  return `Provider #${providerId}`;
}

function productTypeLabel(productType: ProductAggregateItem["productType"]): string {
  return productType === "BASKET" ? "Basket" : "Portfolio";
}

function fallbackProductTitle(item: ProductAggregateItem): string {
  return `${productTypeLabel(item.productType)} #${item.productId}`;
}

function isProductAggregateForSelection(item: ProductAggregateItem, product: ProductSearchItem): boolean {
  return item.productType === product.productType && item.productId === product.productId;
}

function productDisplayName(
  item: ProductAggregateItem,
  productNames: Map<string, string>,
  providerId: number
): string {
  return productNames.get(productLookupKey(providerId, item.productType, item.productId)) ?? fallbackProductTitle(item);
}

function formatSignedNumber(value: number): string {
  if (value === 0) return "0";
  const sign = value > 0 ? "+" : "-";
  return `${sign}${formatNumber(Math.abs(value), 0)}`;
}

function formatSignedCurrency(value: number): string {
  if (value === 0) return "$0";
  const sign = value > 0 ? "+" : "-";
  return `${sign}${formatCurrencyWithCommas(Math.abs(value), 0)}`;
}

function formatCompactCurrency(value: number): string {
  if (!Number.isFinite(value)) return "$0";
  const sign = value < 0 ? "-" : "";
  const absoluteValue = Math.abs(value);
  if (absoluteValue >= 1_000_000_000) return `${sign}$${formatNumber(absoluteValue / 1_000_000_000, 1)}B`;
  if (absoluteValue >= 1_000_000) return `${sign}$${formatNumber(absoluteValue / 1_000_000, 1)}M`;
  if (absoluteValue >= 1_000) return `${sign}$${formatNumber(absoluteValue / 1_000, 0)}K`;
  return `${sign}$${formatNumber(absoluteValue, 0)}`;
}

async function fetchAllAdminProducts(): Promise<ProductSearchItem[]> {
  const first = await productSearchService.searchAdminProducts({ page: 1, pageSize: PAGE_SIZE });
  const all = [...first.items];
  if (first.totalPages <= 1) return all;
  const remaining = await Promise.all(
    Array.from({ length: first.totalPages - 1 }, (_, i) =>
      productSearchService.searchAdminProducts({ page: i + 2, pageSize: PAGE_SIZE })
    )
  );
  remaining.forEach((page) => all.push(...page.items));
  return all;
}

async function loadAdminLookup(): Promise<AdminLookup> {
  const products = await fetchAllAdminProducts();
  const providerNames = new Map<number, string>();
  const productNames = new Map<string, string>();

  products.forEach((product) => {
    providerNames.set(product.providerId, product.providerName);
    productNames.set(
      productLookupKey(product.providerId, product.productType, product.productId),
      product.title
    );
  });

  return { providerNames, productNames, products };
}

async function loadAllProviderLatestProducts(providerIds: number[]): Promise<AllProviderProductAggregateItem[]> {
  const productPages = await Promise.all(
    providerIds.map(async (providerId) => {
      const items = await aggregatesService.getAdminProviderProductAggregates(providerId, "latest");
      return items.map((item) => ({ ...item, providerId }));
    })
  );
  return getLatestAllProviderProductRows(productPages.flat());
}

function buildProvidersByBucket(
  items: AllProviderAggregateItem[],
  timeZone: string
): Map<string, Map<number, AllProviderAggregateItem>> {
  const providersByBucket = new Map<string, Map<number, AllProviderAggregateItem>>();
  items.forEach((item) => {
    const bucket = getLocalDateBucket(item.period, timeZone);
    if (!providersByBucket.has(bucket)) {
      providersByBucket.set(bucket, new Map());
    }
    const providers = providersByBucket.get(bucket)!;
    const existing = providers.get(item.providerId);
    if (!existing || new Date(item.period).getTime() > new Date(existing.period).getTime()) {
      providers.set(item.providerId, item);
    }
  });
  return providersByBucket;
}

function buildProductsByBucket(
  items: ProductAggregateItem[],
  timeZone: string
): Map<string, Map<string, ProductAggregateItem>> {
  const productsByBucket = new Map<string, Map<string, ProductAggregateItem>>();
  items.forEach((item) => {
    const bucket = getLocalDateBucket(item.period, timeZone);
    const key = productKey(item.productType, item.productId);
    if (!productsByBucket.has(bucket)) {
      productsByBucket.set(bucket, new Map());
    }
    const products = productsByBucket.get(bucket)!;
    const existing = products.get(key);
    if (!existing || new Date(item.period).getTime() > new Date(existing.period).getTime()) {
      products.set(key, item);
    }
  });
  return productsByBucket;
}

function buildTotalChart(
  rows: Array<{ period: string; totalAum: number }>,
  timeZone: string,
  latestPeriod: string | null,
  lineLabel = "Total AUM"
): { chartData: ChartDataPoint[]; lines: LineConfig[]; xAxisTicks: XAxisTickConfig[] } {
  const sorted = [...rows].sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());
  const xAxisTicks: XAxisTickConfig[] = [];
  const chartData = sorted.map((row) => {
    const dateOnly = getLocalDateBucket(row.period, timeZone);
    const isLatestPoint = latestPeriod === row.period;
    const value = isLatestPoint ? row.period : dateOnly;
    xAxisTicks.push({
      value,
      label: isLatestPoint ? formatLatestAxisLabel(row.period, timeZone) : formatAxisDateBucket(dateOnly),
    });
    return {
      date: dateOnly,
      fullDate: value,
      total_aum: row.totalAum,
    };
  });

  return {
    chartData,
    lines: [
      {
        dataKey: "total_aum",
        stroke: TOTAL_CHART_COLOR.stroke,
        strokeWidth: 2,
        label: lineLabel,
        swatchClassName: TOTAL_CHART_COLOR.swatchClassName,
      },
    ],
    xAxisTicks,
  };
}

function buildPlatformProviderChart(
  platformAggregates: PlatformAggregateItem[],
  providerAggregates: AllProviderAggregateItem[],
  latestProviderSnapshots: AllProviderAggregateItem[],
  providerNames: Map<number, string>,
  timeZone: string,
  latestPlatformPeriod: string | null
): { chartData: ChartDataPoint[]; lines: LineConfig[]; xAxisTicks: XAxisTickConfig[] } {
  const sortedPlatform = [...platformAggregates].sort(
    (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
  );
  if (sortedPlatform.length === 0) return { chartData: [], lines: [], xAxisTicks: [] };

  const latestProviderRows = latestProviderSnapshots.length > 0
    ? latestProviderSnapshots
    : getLatestProviderRows(providerAggregates);
  const orderedProviderIds = new Set<number>();
  latestProviderRows.forEach((item) => orderedProviderIds.add(item.providerId));
  providerAggregates.forEach((item) => orderedProviderIds.add(item.providerId));

  const dailyProvidersByBucket = buildProvidersByBucket(providerAggregates, timeZone);
  const latestProviderById = new Map<number, AllProviderAggregateItem>();
  latestProviderRows.forEach((item) => latestProviderById.set(item.providerId, item));
  const xAxisTicks: XAxisTickConfig[] = [];

  const chartData: ChartDataPoint[] = sortedPlatform.map((platform) => {
    const dateOnly = getLocalDateBucket(platform.period, timeZone);
    const isLatestPoint = latestPlatformPeriod === platform.period;
    const point: ChartDataPoint = {
      date: dateOnly,
      fullDate: isLatestPoint ? platform.period : dateOnly,
      total_aum: platform.totalAum,
    };
    const periodProviders = dailyProvidersByBucket.get(dateOnly);
    orderedProviderIds.forEach((providerId) => {
      const provider = isLatestPoint ? latestProviderById.get(providerId) : periodProviders?.get(providerId);
      point[providerChartKey(providerId)] = provider?.totalAum ?? null;
    });
    xAxisTicks.push({
      value: point.fullDate ?? point.date,
      label: isLatestPoint ? formatLatestAxisLabel(platform.period, timeZone) : formatAxisDateBucket(dateOnly),
    });
    return point;
  });

  const lines: LineConfig[] = [
    {
      dataKey: "total_aum",
      stroke: TOTAL_CHART_COLOR.stroke,
      strokeWidth: 2,
      label: "Platform AUM",
      swatchClassName: TOTAL_CHART_COLOR.swatchClassName,
    },
    ...Array.from(orderedProviderIds).map((providerId, index) => {
      const color = getProductColor(index);
      return {
        dataKey: providerChartKey(providerId),
        stroke: color.stroke,
        strokeWidth: 1.5,
        label: providerNames.get(providerId) ?? fallbackProviderName(providerId),
        swatchClassName: color.swatchClassName,
      };
    }),
  ];

  return { chartData, lines, xAxisTicks };
}

function buildProviderProductChart(
  providerAggregates: ProviderAggregateItem[],
  productAggregates: ProductAggregateItem[],
  latestProductSnapshots: ProductAggregateItem[],
  productNames: Map<string, string>,
  providerId: number,
  timeZone: string,
  latestProviderPeriod: string | null
): { chartData: ChartDataPoint[]; lines: LineConfig[]; xAxisTicks: XAxisTickConfig[] } {
  const sortedProvider = [...providerAggregates].sort(
    (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
  );
  if (sortedProvider.length === 0) return { chartData: [], lines: [], xAxisTicks: [] };

  const latestRows = getLatestProductRows(latestProductSnapshots.length > 0 ? latestProductSnapshots : productAggregates);
  const orderedProductKeys = new Set<string>();
  latestRows.forEach((item) => orderedProductKeys.add(productKey(item.productType, item.productId)));
  productAggregates.forEach((item) => orderedProductKeys.add(productKey(item.productType, item.productId)));

  const dailyProductsByBucket = buildProductsByBucket(productAggregates, timeZone);
  const latestProductsByBucket = buildProductsByBucket(latestProductSnapshots, timeZone);
  const xAxisTicks: XAxisTickConfig[] = [];

  const chartData: ChartDataPoint[] = sortedProvider.map((provider) => {
    const dateOnly = getLocalDateBucket(provider.period, timeZone);
    const isLatestPoint = latestProviderPeriod === provider.period;
    const point: ChartDataPoint = {
      date: dateOnly,
      fullDate: isLatestPoint ? provider.period : dateOnly,
      total_aum: provider.totalAum,
    };
    const periodProducts = isLatestPoint
      ? latestProductsByBucket.get(dateOnly) ?? dailyProductsByBucket.get(dateOnly)
      : dailyProductsByBucket.get(dateOnly);

    orderedProductKeys.forEach((key) => {
      point[key] = periodProducts?.get(key)?.totalAum ?? null;
    });
    xAxisTicks.push({
      value: point.fullDate ?? point.date,
      label: isLatestPoint ? formatLatestAxisLabel(provider.period, timeZone) : formatAxisDateBucket(dateOnly),
    });
    return point;
  });

  const lines: LineConfig[] = [
    {
      dataKey: "total_aum",
      stroke: TOTAL_CHART_COLOR.stroke,
      strokeWidth: 2,
      label: "Provider AUM",
      swatchClassName: TOTAL_CHART_COLOR.swatchClassName,
    },
    ...Array.from(orderedProductKeys).map((key, index) => {
      const [productType, productIdRaw] = key.split("_") as [ProductAggregateItem["productType"], string];
      const productId = Number(productIdRaw);
      const color = getProductColor(index);
      return {
        dataKey: key,
        stroke: color.stroke,
        strokeWidth: 1.5,
        label: productNames.get(productLookupKey(providerId, productType, productId)) ?? key.replace("_", " #"),
        swatchClassName: color.swatchClassName,
      };
    }),
  ];

  return { chartData, lines, xAxisTicks };
}

interface StatTileProps {
  icon: LucideIcon;
  title: string;
  value: string;
  change?: string | null;
  changeTone?: "positive" | "negative" | "neutral";
}

const StatTile: React.FC<StatTileProps> = ({ icon, title, value, change, changeTone = "neutral" }) => {
  const Icon = icon;
  const isPositive = changeTone === "positive";
  const isNegative = changeTone === "negative";
  const ChangeIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;

  return (
    <div className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator p-5 transition-colors">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg bg-gray-100 dark:bg-rp-surface-raised">
          <Icon className="w-4 h-4 text-rp-amber" />
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {title}
        </span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white font-mono">{value}</p>
      {change && (
        <div className="flex items-center gap-1.5 mt-2">
          <ChangeIcon
            className={`w-3.5 h-3.5 ${
              isPositive ? "text-green-600 dark:text-green-500" : isNegative ? "text-red-600 dark:text-red-500" : "text-gray-500 dark:text-gray-400"
            }`}
          />
          <span
            className={`text-xs font-mono font-semibold ${
              isPositive ? "text-green-600 dark:text-green-500" : isNegative ? "text-red-600 dark:text-red-500" : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {change}
          </span>
        </div>
      )}
    </div>
  );
};

interface AumChartProps {
  chartData: ChartDataPoint[];
  lines: LineConfig[];
  xAxisTicks: XAxisTickConfig[];
  emptyText: string;
}

const AumChart: React.FC<AumChartProps> = ({ chartData, lines, xAxisTicks, emptyText }) => {
  const chartHeight = 360;
  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    data: chartData,
    lines,
    isDarkMode: true,
    height: chartHeight,
    margins: { top: 12, right: 72, bottom: 24, left: 12 },
    yAxisFormatter: (value: number) => {
      if (Math.abs(value) >= 1_000_000) return `$${formatNumber(value / 1_000_000, 1)}M`;
      if (Math.abs(value) >= 1_000) return `$${formatNumber(value / 1_000, 0)}K`;
      return `$${formatNumber(value, 0)}`;
    },
    tooltipFormatter: (value: number) => formatCurrencyWithCommas(value, 0),
    showLegend: false,
    renderingMode: "enhanced",
    xAxisTicks,
  });

  if (chartData.length === 0 || lines.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <BarChart2 className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{emptyText}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="mb-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 text-xs text-gray-600 dark:text-gray-300">
        {lines.map((line) => (
          <div key={line.dataKey} className="flex max-w-[220px] items-center gap-2">
            <span className={`h-0.5 w-5 flex-shrink-0 rounded-full ${line.swatchClassName ?? ""}`} />
            <span className="truncate">{line.label}</span>
          </div>
        ))}
      </div>
      <div ref={containerRef} className="relative overflow-visible" style={{ width: "100%", height: chartHeight }}>
        <svg ref={svgRef} className="block" />
        <div
          ref={tooltipRef}
          className="absolute hidden rounded-md border border-gray-200 dark:border-rp-separator bg-white dark:bg-rp-surface-raised px-3 py-2 shadow-lg pointer-events-none z-[1000]"
        />
      </div>
    </div>
  );
};

const AdminReportingPage: React.FC = () => {
  const [platformAggregates, setPlatformAggregates] = useState<PlatformAggregateItem[]>([]);
  const [latestPlatformSnapshot, setLatestPlatformSnapshot] = useState<PlatformAggregateItem | null>(null);
  const [allProviderAggregates, setAllProviderAggregates] = useState<AllProviderAggregateItem[]>([]);
  const [latestProviderSnapshots, setLatestProviderSnapshots] = useState<AllProviderAggregateItem[]>([]);
  const [allProviderProductSnapshots, setAllProviderProductSnapshots] = useState<AllProviderProductAggregateItem[]>([]);
  const [providerNames, setProviderNames] = useState<Map<number, string>>(new Map());
  const [productNames, setProductNames] = useState<Map<string, string>>(new Map());
  const [products, setProducts] = useState<ProductSearchItem[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [selectedProductKey, setSelectedProductKey] = useState("");
  const [selectedProviderAggregates, setSelectedProviderAggregates] = useState<ProviderAggregateItem[]>([]);
  const [latestSelectedProviderSnapshot, setLatestSelectedProviderSnapshot] = useState<ProviderAggregateItem | null>(null);
  const [selectedProductAggregates, setSelectedProductAggregates] = useState<ProductAggregateItem[]>([]);
  const [latestSelectedProductSnapshots, setLatestSelectedProductSnapshots] = useState<ProductAggregateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drilldownError, setDrilldownError] = useState<string | null>(null);
  const [allProviderProductsError, setAllProviderProductsError] = useState<string | null>(null);
  const loadIdRef = useRef(0);
  const drilldownLoadIdRef = useRef(0);
  const dashboardTimezone = useMemo(() => getDashboardTimezone(), []);

  const load = useCallback(async () => {
    const loadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);
    setAllProviderProductsError(null);

    try {
      const [platformItems, latestPlatform, providerItems, latestProviders, lookup] = await Promise.all([
        aggregatesService.getAdminPlatformAggregates("daily"),
        aggregatesService.getAdminPlatformAggregates("latest"),
        aggregatesService.getAdminAllProviderAggregates("daily"),
        aggregatesService.getAdminAllProviderAggregates("latest"),
        loadAdminLookup().catch((err) => {
          console.error("Failed to load admin reporting lookup:", err);
          return { providerNames: new Map<number, string>(), productNames: new Map<string, string>(), products: [] };
        }),
      ]);

      if (loadIdRef.current !== loadId) return;
      const latestProviderRows = getLatestProviderRows(latestProviders);
      let latestProducts: AllProviderProductAggregateItem[] = [];
      try {
        latestProducts = await loadAllProviderLatestProducts(latestProviderRows.map((provider) => provider.providerId));
      } catch (err) {
        console.error("Failed to load all-provider product aggregates:", err);
        setAllProviderProductsError("Failed to load all-product aggregates.");
      }
      if (loadIdRef.current !== loadId) return;
      setPlatformAggregates(platformItems);
      setLatestPlatformSnapshot(latestPlatform[0] ?? null);
      setAllProviderAggregates(providerItems);
      setLatestProviderSnapshots(latestProviderRows);
      setAllProviderProductSnapshots(latestProducts);
      setProviderNames(lookup.providerNames);
      setProductNames(lookup.productNames);
      setProducts(lookup.products);
    } catch (err) {
      if (loadIdRef.current !== loadId) return;
      console.error("Failed to load admin reporting aggregates:", err);
      setError("Failed to load reporting metrics.");
    } finally {
      if (loadIdRef.current === loadId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (selectedProviderId === null) {
      setSelectedProviderAggregates([]);
      setLatestSelectedProviderSnapshot(null);
      setSelectedProductAggregates([]);
      setLatestSelectedProductSnapshots([]);
      return;
    }

    const loadId = ++drilldownLoadIdRef.current;
    setDrilldownLoading(true);
    setDrilldownError(null);
    setSelectedProviderAggregates([]);
    setLatestSelectedProviderSnapshot(null);
    setSelectedProductAggregates([]);
    setLatestSelectedProductSnapshots([]);

    Promise.all([
      aggregatesService.getAdminProviderAggregates(selectedProviderId, "daily"),
      aggregatesService.getAdminProviderAggregates(selectedProviderId, "latest"),
      aggregatesService.getAdminProviderProductAggregates(selectedProviderId, "daily"),
      aggregatesService.getAdminProviderProductAggregates(selectedProviderId, "latest"),
    ])
      .then(([providerItems, latestProvider, productItems, latestProducts]) => {
        if (drilldownLoadIdRef.current !== loadId) return;
        setSelectedProviderAggregates(providerItems);
        setLatestSelectedProviderSnapshot(latestProvider[0] ?? null);
        setSelectedProductAggregates(productItems);
        setLatestSelectedProductSnapshots(latestProducts);
      })
      .catch((err) => {
        if (drilldownLoadIdRef.current !== loadId) return;
        console.error("Failed to load admin provider drilldown:", err);
        setDrilldownError("Failed to load provider drilldown.");
      })
      .finally(() => {
        if (drilldownLoadIdRef.current === loadId) setDrilldownLoading(false);
      });
  }, [selectedProviderId]);

  const mergedPlatformAggregates = useMemo(
    () => mergeLatest(platformAggregates, latestPlatformSnapshot),
    [platformAggregates, latestPlatformSnapshot]
  );
  const latestPlatform = useMemo(
    () => latestPlatformSnapshot ?? getLatestByPeriod(platformAggregates),
    [latestPlatformSnapshot, platformAggregates]
  );
  const previousPlatform = useMemo(
    () => getPreviousByPeriod(mergedPlatformAggregates),
    [mergedPlatformAggregates]
  );
  const latestProviders = useMemo(
    () => latestProviderSnapshots.length > 0 ? latestProviderSnapshots : getLatestProviderRows(allProviderAggregates),
    [latestProviderSnapshots, allProviderAggregates]
  );
  const allProviderLatestProducts = useMemo(
    () => getLatestAllProviderProductRows(allProviderProductSnapshots),
    [allProviderProductSnapshots]
  );
  const providerOptions = useMemo(() => {
    const ids = new Set<number>();
    latestProviders.forEach((provider) => ids.add(provider.providerId));
    products.forEach((product) => ids.add(product.providerId));
    return Array.from(ids)
      .map((providerId) => ({
        providerId,
        name: providerNames.get(providerId) ?? fallbackProviderName(providerId),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [latestProviders, products, providerNames]);
  const productOptions = useMemo(
    () => [...products].sort((a, b) => {
      const providerCompare = a.providerName.localeCompare(b.providerName);
      if (providerCompare !== 0) return providerCompare;
      return a.title.localeCompare(b.title);
    }),
    [products]
  );
  const selectedProduct = useMemo(
    () => selectedProductKey
      ? productOptions.find((item) => productSelectKey(item) === selectedProductKey) ?? null
      : null,
    [productOptions, selectedProductKey]
  );
  const totalProviderAum = useMemo(
    () => latestProviders.reduce((sum, provider) => sum + Math.max(0, provider.totalAum), 0),
    [latestProviders]
  );
  const mergedSelectedProviderAggregates = useMemo(
    () => mergeLatest(selectedProviderAggregates, latestSelectedProviderSnapshot),
    [selectedProviderAggregates, latestSelectedProviderSnapshot]
  );
  const latestSelectedProvider = useMemo(
    () => latestSelectedProviderSnapshot ?? getLatestByPeriod(selectedProviderAggregates),
    [latestSelectedProviderSnapshot, selectedProviderAggregates]
  );
  const selectedProviderLatestProducts = useMemo(() => {
    if (latestSelectedProviderSnapshot) {
      return getProductsForProviderPeriod(
        latestSelectedProductSnapshots,
        latestSelectedProviderSnapshot.period
      ).sort((a, b) => b.totalAum - a.totalAum);
    }
    if (latestSelectedProductSnapshots.length > 0) {
      return [...latestSelectedProductSnapshots].sort((a, b) => b.totalAum - a.totalAum);
    }
    return getLatestProductRows(selectedProductAggregates);
  }, [latestSelectedProductSnapshots, latestSelectedProviderSnapshot, selectedProductAggregates]);
  const selectedProviderProductAum = useMemo(
    () => selectedProviderLatestProducts.reduce((sum, product) => sum + Math.max(0, product.totalAum), 0),
    [selectedProviderLatestProducts]
  );
  const allProviderProductAum = useMemo(
    () => allProviderLatestProducts.reduce((sum, product) => sum + Math.max(0, product.totalAum), 0),
    [allProviderLatestProducts]
  );
  const selectedProductAggregatesForScope = useMemo(
    () => selectedProduct
      ? selectedProductAggregates.filter((item) => isProductAggregateForSelection(item, selectedProduct))
      : [],
    [selectedProduct, selectedProductAggregates]
  );
  const latestSelectedProductSnapshotsForScope = useMemo(
    () => selectedProduct
      ? latestSelectedProductSnapshots.filter((item) => isProductAggregateForSelection(item, selectedProduct))
      : [],
    [selectedProduct, latestSelectedProductSnapshots]
  );
  const latestSelectedProduct = useMemo(() => {
    if (!selectedProduct) return null;
    if (latestSelectedProviderSnapshot) {
      return getProductsForProviderPeriod(
        latestSelectedProductSnapshotsForScope,
        latestSelectedProviderSnapshot.period
      )[0] ?? getLatestByPeriod(latestSelectedProductSnapshotsForScope) ?? getLatestByPeriod(selectedProductAggregatesForScope);
    }
    return getLatestByPeriod(latestSelectedProductSnapshotsForScope) ?? getLatestByPeriod(selectedProductAggregatesForScope);
  }, [
    selectedProduct,
    latestSelectedProviderSnapshot,
    latestSelectedProductSnapshotsForScope,
    selectedProductAggregatesForScope,
  ]);
  const mergedSelectedProductAggregates = useMemo(
    () => mergeLatest(selectedProductAggregatesForScope, latestSelectedProduct),
    [selectedProductAggregatesForScope, latestSelectedProduct]
  );
  const selectedProductAumProviderShare = latestSelectedProvider && latestSelectedProvider.totalAum > 0 && latestSelectedProduct
    ? (latestSelectedProduct.totalAum / latestSelectedProvider.totalAum) * 100
    : 0;
  const selectedProviderVisibleProducts = useMemo(
    () => selectedProviderId === null
      ? []
      : selectedProviderLatestProducts.map((item) => ({ ...item, providerId: selectedProviderId })),
    [selectedProviderId, selectedProviderLatestProducts]
  );
  const visibleProductRows: AllProviderProductAggregateItem[] = selectedProduct && selectedProviderId !== null
    ? (latestSelectedProduct ? [{ ...latestSelectedProduct, providerId: selectedProviderId }] : [])
    : selectedProviderId !== null ? selectedProviderVisibleProducts : allProviderLatestProducts;

  const platformAumChange = latestPlatform && previousPlatform
    ? latestPlatform.totalAum - previousPlatform.totalAum
    : null;
  const platformUserChange = latestPlatform && previousPlatform
    ? latestPlatform.numOfUsers - previousPlatform.numOfUsers
    : null;
  const platformProviderChange = latestPlatform && previousPlatform
    ? latestPlatform.numOfProviders - previousPlatform.numOfProviders
    : null;
  const multiProductUserChange = latestPlatform && previousPlatform
    ? latestPlatform.numOfUsersWithMultiProductStrategies - previousPlatform.numOfUsersWithMultiProductStrategies
    : null;

  const platformChart = useMemo(
    () => buildPlatformProviderChart(
      mergedPlatformAggregates,
      allProviderAggregates,
      latestProviders,
      providerNames,
      dashboardTimezone,
      latestPlatformSnapshot?.period ?? null
    ),
    [mergedPlatformAggregates, allProviderAggregates, latestProviders, providerNames, dashboardTimezone, latestPlatformSnapshot]
  );

  const providerChart = useMemo(() => {
    if (selectedProviderId === null) return { chartData: [], lines: [], xAxisTicks: [] };
    return buildProviderProductChart(
      mergedSelectedProviderAggregates,
      selectedProductAggregates,
      latestSelectedProductSnapshots,
      productNames,
      selectedProviderId,
      dashboardTimezone,
      latestSelectedProviderSnapshot?.period ?? null
    );
  }, [
    selectedProviderId,
    mergedSelectedProviderAggregates,
    selectedProductAggregates,
    latestSelectedProductSnapshots,
    productNames,
    dashboardTimezone,
    latestSelectedProviderSnapshot,
  ]);
  const productChart = useMemo(() => {
    if (!selectedProduct) return { chartData: [], lines: [], xAxisTicks: [] };
    return buildTotalChart(
      mergedSelectedProductAggregates,
      dashboardTimezone,
      latestSelectedProduct?.period ?? null,
      "Product AUM"
    );
  }, [selectedProduct, mergedSelectedProductAggregates, dashboardTimezone, latestSelectedProduct]);

  const selectedProviderName = selectedProviderId !== null
    ? providerNames.get(selectedProviderId) ?? fallbackProviderName(selectedProviderId)
    : "Select a provider";
  const selectedProductName = selectedProduct ? selectedProduct.title : "Select a product";
  const providerCountText = `${formatNumber(latestProviders.length, 0)} providers`;
  const productCountText = selectedProduct ? selectedProductName : `${formatNumber(visibleProductRows.length, 0)} products`;
  const selectedProviderAumShare = latestPlatform && latestPlatform.totalAum > 0 && latestSelectedProvider
    ? (latestSelectedProvider.totalAum / latestPlatform.totalAum) * 100
    : 0;
  const scopedChart = selectedProduct ? productChart : selectedProviderId === null ? platformChart : providerChart;
  const chartTitle = selectedProduct
    ? `${selectedProductName} AUM`
    : selectedProviderId === null ? "Platform AUM" : `${selectedProviderName} AUM`;
  const chartEmptyText = selectedProduct
    ? "No product AUM data available yet."
    : selectedProviderId === null ? "No platform AUM data available yet." : "No provider AUM data available yet.";
  const productTableTitle = selectedProduct ? "Product Detail" : "Product Breakdown";
  const productTableAriaLabel = selectedProduct ? "Product detail" : "Product breakdown";
  const lastUpdatedPeriod = selectedProduct
    ? latestSelectedProduct?.period
    : selectedProviderId === null ? latestPlatform?.period : latestSelectedProvider?.period;
  const hasActiveFilters = selectedProviderId !== null || selectedProductKey !== "";

  const handleProviderChange = (providerIdRaw: string) => {
    setSelectedProductKey("");
    setSelectedProviderId(providerIdRaw ? Number(providerIdRaw) : null);
  };

  const handleProductChange = (key: string) => {
    setSelectedProductKey(key);
    if (!key) return;
    const product = productOptions.find((item) => productSelectKey(item) === key);
    setSelectedProviderId(product ? product.providerId : null);
  };

  const handleClearFilters = () => {
    setSelectedProductKey("");
    setSelectedProviderId(null);
  };

  return (
    <AdminPageLayout
      title="Reporting"
      description="Platform, provider, and product aggregate reporting"
      actions={
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      }
    >
      <div className="space-y-6 transition-colors duration-300">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between gap-4">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            <button
              onClick={load}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {lastUpdatedPeriod && (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Last updated {formatPeriodWithTime(lastUpdatedPeriod, dashboardTimezone)}
          </p>
        )}

        {loading && platformAggregates.length === 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator p-5 animate-pulse"
                >
                  <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/3 mb-4" />
                  <div className="h-7 bg-gray-200 dark:bg-gray-800 rounded w-1/2" />
                </div>
              ))}
            </div>
            <div className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator animate-pulse h-96" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-3 items-end">
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Provider
                </span>
                <select
                  value={selectedProviderId ?? ""}
                  onChange={(event) => handleProviderChange(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white"
                >
                  <option value="">All providers</option>
                  {providerOptions.map((provider) => (
                    <option key={provider.providerId} value={provider.providerId}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Product
                </span>
                <select
                  value={selectedProductKey}
                  onChange={(event) => handleProductChange(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface-raised dark:text-white"
                >
                  <option value="">Select a product</option>
                  {productOptions.map((product) => (
                    <option key={productSelectKey(product)} value={productSelectKey(product)}>
                      {product.providerName} - {product.title}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                className="mt-1 inline-flex h-10 w-full md:w-auto items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
              >
                <X className="w-4 h-4" />
                Clear filters
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {selectedProduct ? (
                <>
                  <StatTile
                    icon={DollarSign}
                    title="Product AUM"
                    value={latestSelectedProduct ? formatCurrencyWithCommas(latestSelectedProduct.totalAum, 0) : "--"}
                  />
                  <StatTile
                    icon={Users}
                    title="Unique Users"
                    value={latestSelectedProduct ? formatNumber(latestSelectedProduct.numOfUsers, 0) : "--"}
                  />
                  <StatTile
                    icon={Package}
                    title="Product Type"
                    value={productTypeLabel(selectedProduct.productType)}
                  />
                  <StatTile
                    icon={Building2}
                    title="Provider AUM %"
                    value={formatPercentage(selectedProductAumProviderShare, 1)}
                  />
                </>
              ) : selectedProviderId === null ? (
                <>
                  <StatTile
                    icon={DollarSign}
                    title="Platform AUM"
                    value={latestPlatform ? formatCurrencyWithCommas(latestPlatform.totalAum, 0) : "--"}
                    change={platformAumChange === null ? null : formatSignedCurrency(platformAumChange)}
                    changeTone={platformAumChange === null ? "neutral" : platformAumChange > 0 ? "positive" : platformAumChange < 0 ? "negative" : "neutral"}
                  />
                  <StatTile
                    icon={Users}
                    title="Unique Users"
                    value={latestPlatform ? formatNumber(latestPlatform.numOfUsers, 0) : "--"}
                    change={platformUserChange === null ? null : formatSignedNumber(platformUserChange)}
                    changeTone={platformUserChange === null ? "neutral" : platformUserChange > 0 ? "positive" : platformUserChange < 0 ? "negative" : "neutral"}
                  />
                  <StatTile
                    icon={Building2}
                    title="Providers"
                    value={latestPlatform ? formatNumber(latestPlatform.numOfProviders, 0) : "--"}
                    change={platformProviderChange === null ? null : formatSignedNumber(platformProviderChange)}
                    changeTone={platformProviderChange === null ? "neutral" : platformProviderChange > 0 ? "positive" : platformProviderChange < 0 ? "negative" : "neutral"}
                  />
                  <StatTile
                    icon={Package}
                    title="Multi-Product Users"
                    value={latestPlatform ? formatNumber(latestPlatform.numOfUsersWithMultiProductStrategies, 0) : "--"}
                    change={multiProductUserChange === null ? null : formatSignedNumber(multiProductUserChange)}
                    changeTone={multiProductUserChange === null ? "neutral" : multiProductUserChange > 0 ? "positive" : multiProductUserChange < 0 ? "negative" : "neutral"}
                  />
                </>
              ) : (
                <>
                  <StatTile
                    icon={DollarSign}
                    title="Provider AUM"
                    value={latestSelectedProvider ? formatCurrencyWithCommas(latestSelectedProvider.totalAum, 0) : "--"}
                  />
                  <StatTile
                    icon={Users}
                    title="Unique Users"
                    value={latestSelectedProvider ? formatNumber(latestSelectedProvider.numOfUsers, 0) : "--"}
                  />
                  <StatTile
                    icon={Package}
                    title="Products"
                    value={formatNumber(selectedProviderLatestProducts.length, 0)}
                  />
                  <StatTile
                    icon={Building2}
                    title="Platform AUM %"
                    value={formatPercentage(selectedProviderAumShare, 1)}
                  />
                </>
              )}
            </div>

            <div className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {chartTitle}
                </span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Daily + Latest
                </span>
              </div>
              <div className="p-2">
                {selectedProviderId !== null && drilldownError && (
                  <div className="m-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                    <p className="text-sm text-red-700 dark:text-red-400">{drilldownError}</p>
                  </div>
                )}
                {selectedProviderId !== null && drilldownLoading && selectedProviderAggregates.length === 0 ? (
                  <div className="p-6 space-y-4 animate-pulse">
                    <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/3" />
                    <div className="h-64 bg-gray-200 dark:bg-gray-800 rounded" />
                  </div>
                ) : (
                  <AumChart
                    chartData={scopedChart.chartData}
                    lines={scopedChart.lines}
                    xAxisTicks={scopedChart.xAxisTicks}
                    emptyText={chartEmptyText}
                  />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
              {selectedProviderId === null && (
              <div className="xl:col-span-2 bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Provider Comparison
                  </span>
                  <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                    {providerCountText}
                  </span>
                </div>
                <div className="overflow-hidden">
                  <table aria-label="Provider comparison" className="w-full table-fixed">
                    <colgroup>
                      <col className="w-[42%]" />
                      <col className="w-[18%]" />
                      <col className="w-[24%]" />
                      <col className="w-[16%]" />
                    </colgroup>
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-rp-separator">
                        <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Provider</th>
                        <th className="px-2 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-normal leading-tight">Unique Users</th>
                        <th className="px-2 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">AUM</th>
                        <th className="px-2 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">AUM %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latestProviders.length > 0 ? (
                        latestProviders.map((provider) => {
                          const selected = selectedProviderId === provider.providerId;
                          const share = totalProviderAum > 0 ? (provider.totalAum / totalProviderAum) * 100 : 0;
                          const name = providerNames.get(provider.providerId) ?? fallbackProviderName(provider.providerId);
                          return (
                            <tr
                              key={provider.providerId}
                              className={`border-b border-gray-200 dark:border-rp-separator last:border-b-0 transition-colors ${
                                selected ? "bg-rp-amber/10" : "hover:bg-gray-50 dark:hover:bg-rp-surface-raised"
                              }`}
                            >
                              <td className="px-3 py-3 min-w-0">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedProductKey("");
                                    setSelectedProviderId(provider.providerId);
                                  }}
                                  aria-pressed={selected}
                                  className="block w-full truncate text-left text-sm font-semibold text-gray-900 dark:text-white hover:text-rp-amber transition-colors"
                                >
                                  {name}
                                </button>
                                <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                                  ID {provider.providerId}
                                </div>
                              </td>
                              <td className="px-2 py-3 text-right font-mono tabular-nums text-xs text-gray-900 dark:text-white">
                                {formatNumber(provider.numOfUsers, 0)}
                              </td>
                              <td
                                title={formatCurrencyWithCommas(provider.totalAum, 0)}
                                className="px-2 py-3 text-right font-mono tabular-nums text-xs font-semibold text-gray-900 dark:text-white"
                              >
                                {formatCompactCurrency(provider.totalAum)}
                              </td>
                              <td className="px-2 py-3 text-right font-mono tabular-nums text-xs text-gray-600 dark:text-gray-300">
                                {formatPercentage(share, 1)}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                            No provider aggregate data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              )}

              <div className={`${selectedProviderId === null ? "xl:col-span-3" : "xl:col-span-5"} space-y-6`}>
                <div className="bg-white dark:bg-rp-surface rounded-lg border border-gray-200 dark:border-rp-separator overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-rp-separator">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {productTableTitle}
                    </span>
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                      {productCountText}
                    </span>
                  </div>
                  {selectedProviderId === null && allProviderProductsError && (
                    <div className="m-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <p className="text-sm text-red-700 dark:text-red-400">{allProviderProductsError}</p>
                    </div>
                  )}
                  <div className="overflow-hidden">
                    <table aria-label={productTableAriaLabel} className="w-full table-fixed">
                      <colgroup>
                        <col className="w-[40%]" />
                        <col className="w-[16%]" />
                        <col className="w-[14%]" />
                        <col className="w-[20%]" />
                        <col className="w-[10%]" />
                      </colgroup>
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-rp-separator">
                          <th className="px-3 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Product</th>
                          <th className="px-2 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Type</th>
                          <th className="px-2 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Users</th>
                          <th className="px-2 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">AUM</th>
                          <th className="px-2 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleProductRows.length > 0 ? (
                          visibleProductRows.map((item, index) => {
                            const key = productLookupKey(item.providerId, item.productType, item.productId);
                            const name = productDisplayName(item, productNames, item.providerId);
                            const providerName = providerNames.get(item.providerId) ?? fallbackProviderName(item.providerId);
                            const share = selectedProduct
                              ? selectedProductAumProviderShare
                              : selectedProviderId !== null
                                ? selectedProviderProductAum > 0 ? (item.totalAum / selectedProviderProductAum) * 100 : 0
                                : allProviderProductAum > 0 ? (item.totalAum / allProviderProductAum) * 100 : 0;
                            const isSelectedProductRow = selectedProductKey === key;
                            return (
                              <tr
                                key={key}
                                className={`border-b border-gray-200 dark:border-rp-separator last:border-b-0 transition-colors ${
                                  isSelectedProductRow ? "bg-rp-amber/10" : "hover:bg-gray-50 dark:hover:bg-rp-surface-raised"
                                }`}
                              >
                                <td className="px-3 py-3 min-w-0">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getProductColor(index).swatchClassName}`} />
                                    <div className="min-w-0">
                                      <div className="truncate text-sm font-medium text-gray-900 dark:text-white">{name}</div>
                                      {selectedProviderId === null && (
                                        <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                                          {providerName}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-2 py-3 text-right">
                                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-rp-surface-raised px-2 py-0.5 rounded">
                                    {productTypeLabel(item.productType)}
                                  </span>
                                </td>
                                <td className="px-2 py-3 text-right font-mono tabular-nums text-sm text-gray-900 dark:text-white">
                                  {formatNumber(item.numOfUsers, 0)}
                                </td>
                                <td className="px-2 py-3 text-right font-mono tabular-nums text-sm font-semibold text-gray-900 dark:text-white">
                                  {formatCurrencyWithCommas(item.totalAum, 0)}
                                </td>
                                <td className="px-2 py-3 text-right font-mono tabular-nums text-sm text-gray-600 dark:text-gray-300">
                                  {formatPercentage(share, 1)}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                              No product aggregate data available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                        {!selectedProduct && selectedProviderId !== null && selectedProviderLatestProducts.length > 0 && latestSelectedProvider && (
                        <tfoot>
                          <tr className="border-t border-gray-200 dark:border-rp-separator">
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white">
                              Total
                            </td>
                            <td />
                            <td className="px-4 py-3 text-right font-mono tabular-nums text-sm font-semibold text-gray-900 dark:text-white">
                              {formatNumber(latestSelectedProvider.numOfUsers, 0)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono tabular-nums text-sm font-bold text-gray-900 dark:text-white">
                              {formatCurrencyWithCommas(selectedProviderProductAum, 0)}
                            </td>
                            <td className="px-4 py-3 text-right font-mono tabular-nums text-sm text-gray-600 dark:text-gray-300">
                              100%
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default AdminReportingPage;
